import { isFileSystemAccessSupported } from "./uploadFileSystemAccess";
import {
  uploadDiagnosticDurationMs,
  uploadDiagnosticLog,
  uploadDiagnosticLogThrottled,
  uploadDiagnosticStart,
} from "./uploadDiagnostics";

const DB_NAME = "infostore-upload-v2";
const DB_VERSION = 2;
const JOB_STORE = "uploadJobs";
const ENTRY_STORE = "uploadEntries";
const ENTRY_JOB_STATUS_INDEX = "byJobStatus";
const SUCCESS_STATUS = "COMPLETE";
const CANCELLING_STATUS = "CANCELLING";
const CANCELLED_STATUS = "CANCELLED";
const TIMED_OUT_STATUS = "TIMED_OUT";
const ABANDONED_STATUS = "ABANDONED";
const TERMINAL_ENTRY_STATUSES = new Set(["COMPLETE", "ERROR", "FAILED", "CANCELLED", "TIMED_OUT", "ABANDONED"]);
const TERMINAL_JOB_STATUSES = new Set(["COMPLETE", "COMPLETE_WITH_ERRORS", "FAILED", "CANCELLED", "TIMED_OUT", "ABANDONED"]);
const VISIBLE_LOCAL_JOB_STATUSES = new Set([...TERMINAL_JOB_STATUSES, CANCELLING_STATUS]);
const RETRYABLE_JOB_STATUSES = new Set(["COMPLETE_WITH_ERRORS", "FAILED", "CANCELLED", "TIMED_OUT", "ABANDONED"]);
const DEFAULT_LOCAL_LEASE_TIMEOUT_MS = 120000;
const SLOW_LOCAL_SNAPSHOT_READ_MS = 200;
const SLOW_INDEXEDDB_WRITE_MS = 150;
const SLOW_INDEXEDDB_READ_MS = 250;
const SLOW_LOCAL_SNAPSHOT_LOG_THROTTLE_MS = 60000;
const SLOW_INDEXEDDB_LOG_THROTTLE_MS = 30000;
const LOCAL_ENTRY_REWRITE_LIMIT = 250;
const SOURCE_HANDLE_PERMISSION = {
  AVAILABLE: "AVAILABLE",
  NEEDS_RESELECT: "NEEDS_RESELECT",
  NOT_REQUIRED: "NOT_REQUIRED",
  PERMISSION_DENIED: "PERMISSION_DENIED",
  UNSUPPORTED: "UNSUPPORTED",
  UNKNOWN: "UNKNOWN",
};
let cachedDbPromise = null;
let cachedIndexedDb = null;

function clampByteProgress(uploadedBytes, totalBytes) {
  const total = Math.max(0, Number(totalBytes || 0));
  const uploaded = Math.max(0, Math.min(total || Number.MAX_SAFE_INTEGER, Number(uploadedBytes || 0)));
  return {
    uploadedBytes: uploaded,
    progressPercent: total > 0 ? Math.max(0, Math.min(100, Math.round((uploaded / total) * 100))) : null,
  };
}

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function openUploadDb() {
  if (!window.indexedDB) {
    return Promise.reject(new Error("IndexedDB is not available"));
  }
  if (cachedDbPromise && cachedIndexedDb === window.indexedDB) {
    return cachedDbPromise;
  }
  cachedIndexedDb = window.indexedDB;
  const request = window.indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    const db = request.result;
    if (!db.objectStoreNames.contains(JOB_STORE)) {
      db.createObjectStore(JOB_STORE, { keyPath: "jobId" });
    }
    let entryStore = null;
    if (!db.objectStoreNames.contains(ENTRY_STORE)) {
      entryStore = db.createObjectStore(ENTRY_STORE, { keyPath: ["jobId", "clientEntryId"] });
    } else {
      entryStore = request.transaction.objectStore(ENTRY_STORE);
    }
    if (entryStore && !entryStore.indexNames.contains(ENTRY_JOB_STATUS_INDEX)) {
      entryStore.createIndex(ENTRY_JOB_STATUS_INDEX, ["jobId", "clientStatus"], { unique: false });
    }
  };
  cachedDbPromise = requestToPromise(request).then((db) => {
    db.onversionchange = () => {
      db.close();
      cachedDbPromise = null;
      cachedIndexedDb = null;
    };
    return db;
  }).catch((error) => {
    cachedDbPromise = null;
    cachedIndexedDb = null;
    throw error;
  });
  return cachedDbPromise;
}

async function withStore(storeNames, mode, callback) {
  const db = await openUploadDb();
  return await new Promise((resolve, reject) => {
    const tx = db.transaction(storeNames, mode);
    const stores = Array.isArray(storeNames)
      ? storeNames.map((name) => tx.objectStore(name))
      : tx.objectStore(storeNames);
    let callbackResult;
    tx.oncomplete = () => {
      Promise.resolve(callbackResult).then(resolve).catch(reject);
    };
    tx.onerror = () => {
      reject(tx.error);
    };
    try {
      callbackResult = callback(stores);
    } catch (error) {
      reject(error);
    }
  });
}

function mergeEntryUpdate(current, updates) {
  const { incrementAttempt, ...persistedUpdates } = updates;
  const currentIsTerminal = TERMINAL_ENTRY_STATUSES.has(current.clientStatus);
  const nextStatus = persistedUpdates.clientStatus;
  const nextIsTerminal = TERMINAL_ENTRY_STATUSES.has(nextStatus);
  const attemptCount = incrementAttempt ? (current.attemptCount || 0) + 1 : current.attemptCount || 0;

  if (currentIsTerminal && (!nextStatus || !nextIsTerminal)) {
    return {
      ...current,
      attemptCount,
      lastUpdatedAt: new Date().toISOString(),
    };
  }

  return {
    ...current,
    ...persistedUpdates,
    attemptCount,
    lastUpdatedAt: new Date().toISOString(),
  };
}

function entryStatusForTerminalJob(jobStatus) {
  if (jobStatus === "COMPLETE") {
    return "COMPLETE";
  }
  if (jobStatus === "CANCELLED") {
    return "CANCELLED";
  }
  if (jobStatus === "TIMED_OUT") {
    return "TIMED_OUT";
  }
  if (jobStatus === "ABANDONED") {
    return "ABANDONED";
  }
  return "ERROR";
}

function newLeaseOwnerId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function uploadJobEntryKeyRange(jobId) {
  if (window.IDBKeyRange?.bound) {
    return window.IDBKeyRange.bound([jobId], [jobId, "\uffff"]);
  }
  return undefined;
}

function uploadJobStatusKeyRange(jobId, status) {
  if (window.IDBKeyRange?.only) {
    return window.IDBKeyRange.only([jobId, status]);
  }
  return undefined;
}

async function observedIndexedDbOperation(event, context, callback) {
  const startedAt = uploadDiagnosticStart();
  try {
    const result = await callback();
    const durationMs = uploadDiagnosticDurationMs(startedAt);
    const slowThresholdMs = event.includes("snapshot") || event.includes("read") || event.includes("get")
      ? SLOW_INDEXEDDB_READ_MS
      : SLOW_INDEXEDDB_WRITE_MS;
    if (durationMs > slowThresholdMs) {
      uploadDiagnosticLogThrottled("warn", `${event}-slow`, `${event}:${context?.uploadJobId || "global"}`, {
        ...context,
        durationMs,
        slowThresholdMs,
      }, SLOW_INDEXEDDB_LOG_THROTTLE_MS);
    }
    return result;
  } catch (error) {
    uploadDiagnosticLog("warn", `${event}-failed`, {
      ...context,
      durationMs: uploadDiagnosticDurationMs(startedAt),
    }, error);
    throw error;
  }
}

export function supportsDurableUploadHandles() {
  return typeof window !== "undefined" && isFileSystemAccessSupported(window);
}

function sourceHandlePermissionState(entry) {
  if (entry.entryType !== "FILE") {
    return SOURCE_HANDLE_PERMISSION.NOT_REQUIRED;
  }
  if (entry.sourceHandlePermissionState) {
    return entry.sourceHandlePermissionState;
  }
  const sourceHandle = entry.sourceHandle || entry.sourceItem?.sourceHandle || entry.sourceItem?.fileHandle;
  if (sourceHandle) {
    return SOURCE_HANDLE_PERMISSION.UNKNOWN;
  }
  return supportsDurableUploadHandles()
    ? SOURCE_HANDLE_PERMISSION.NEEDS_RESELECT
    : SOURCE_HANDLE_PERMISSION.UNSUPPORTED;
}

function visibleLocalJobStatus(status) {
  return VISIBLE_LOCAL_JOB_STATUSES.has(status) ? status : null;
}

function normalizeLocalEntryStatus(status) {
  if (status === "FAILED") {
    return "ERROR";
  }
  if (status === "QUEUED" || status === "CREATING_FILE" || status === "UPLOADING" || status === "FINALISING" || status === "CREATING_FOLDER") {
    return "IN_PROGRESS";
  }
  return status || "PENDING";
}

function displayLocalEntryStatus(entry, uploadedBytes) {
  if (
    entry?.entryType === "FILE" &&
    ["QUEUED", "CREATING_FILE"].includes(entry.clientStatus) &&
    Number(uploadedBytes || 0) <= 0
  ) {
    return "WAITING";
  }
  return normalizeLocalEntryStatus(entry?.clientStatus);
}

function localLogStatusRank(log) {
  if (log?.status === "IN_PROGRESS") {
    return 0;
  }
  if (log?.status === "WAITING" || log?.status === "PENDING") {
    return 1;
  }
  if (log?.status === SUCCESS_STATUS) {
    return 3;
  }
  return 2;
}

function compareLocalLogs(left, right) {
  const statusRank = localLogStatusRank(left) - localLogStatusRank(right);
  if (statusRank !== 0) {
    return statusRank;
  }
  return Number(left.id || 0) - Number(right.id || 0);
}

function localEntryUploadedBytes(entry) {
  if (entry.entryType !== "FILE") {
    return null;
  }
  const total = Math.max(0, Number(entry.size || 0));
  if (entry.clientStatus === SUCCESS_STATUS || normalizeLocalEntryStatus(entry.clientStatus) === SUCCESS_STATUS) {
    return total;
  }
  return clampByteProgress(entry.uploadedBytes, total).uploadedBytes;
}

function projectLocalLogEntry(entry) {
  const uploadedBytes = localEntryUploadedBytes(entry);
  const status = displayLocalEntryStatus(entry, uploadedBytes);
  return {
    id: entry.uploadJobLogId,
    uploadJobId: entry.jobId,
    clientEntryId: entry.clientEntryId,
    entryType: entry.entryType,
    itemId: entry.itemId,
    name: entry.name,
    relativePath: entry.relativePath,
    size: entry.size,
    mimeType: entry.mimeType,
    status,
    errorCode: entry.errorCode,
    errorMessage: entry.errorMessage,
    technicalMessage: entry.technicalMessage,
    uploadedBytes,
    progressPercent: entry.entryType === "FILE" && status === SUCCESS_STATUS ? 100 : entry.progressPercent,
    progressUpdatedAt: entry.progressUpdatedAt,
    lastUpdated: entry.lastUpdatedAt,
  };
}

function rawStatusesForLogFilter(status) {
  if (!status) {
    return null;
  }
  if (status === "ERROR") {
    return new Set(["ERROR", "FAILED"]);
  }
  if (status === "IN_PROGRESS") {
    return new Set(["UPLOADING", "FINALISING", "CREATING_FOLDER", "IN_PROGRESS"]);
  }
  if (status === "PENDING") {
    return new Set(["PENDING", "QUEUED", "CREATING_FILE"]);
  }
  return new Set([status]);
}

function emptyLocalEntrySummary(job = null) {
  return {
    clientJobStatus: visibleLocalJobStatus(job?.clientJobStatus),
    totalEntryCount: 0,
    completedEntryCount: 0,
    failedEntryCount: 0,
    totalFileCount: 0,
    completedFileCount: 0,
    failedFileCount: 0,
    totalFolderCount: 0,
    completedFolderCount: 0,
    failedFolderCount: 0,
    totalBytes: 0,
    uploadedBytes: 0,
    progressPercent: null,
    progressUpdatedAt: job?.progressUpdatedAt,
  };
}

function summarizeLocalEntries(entries, job = null) {
  const summary = emptyLocalEntrySummary(job);
  entries.forEach((entry) => {
    const status = normalizeLocalEntryStatus(entry.clientStatus);
    const isFile = entry.entryType === "FILE";
    const size = Math.max(0, Number(entry.size || 0));
    summary.totalEntryCount += 1;
    if (isFile) {
      summary.totalFileCount += 1;
      summary.totalBytes += size;
      summary.uploadedBytes += localEntryUploadedBytes(entry) || 0;
    } else if (entry.entryType === "FOLDER") {
      summary.totalFolderCount += 1;
    }
    if (status === SUCCESS_STATUS) {
      summary.completedEntryCount += 1;
      if (isFile) {
        summary.completedFileCount += 1;
      } else if (entry.entryType === "FOLDER") {
        summary.completedFolderCount += 1;
      }
    } else if (status === "ERROR" || status === CANCELLED_STATUS || status === TIMED_OUT_STATUS || status === ABANDONED_STATUS) {
      summary.failedEntryCount += 1;
      if (isFile) {
        summary.failedFileCount += 1;
      } else if (entry.entryType === "FOLDER") {
        summary.failedFolderCount += 1;
      }
    }
  });
  if (summary.totalEntryCount > 0) {
    const fileProgressUnits = summary.totalBytes > 0 && summary.totalFileCount > 0
      ? summary.totalFileCount * (summary.uploadedBytes / summary.totalBytes)
      : summary.completedFileCount + summary.failedFileCount;
    const completedUnits = summary.completedFolderCount + summary.failedFolderCount + fileProgressUnits;
    summary.progressPercent = Math.max(0, Math.min(100, Math.round((completedUnits / summary.totalEntryCount) * 100)));
  }
  return summary;
}

async function readLocalUploadEntries(jobId) {
  return await withStore(ENTRY_STORE, "readonly", (entries) => (
    new Promise((resolve, reject) => {
      const found = [];
      const request = entries.openCursor(uploadJobEntryKeyRange(jobId));
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve(found);
          return;
        }
        if (cursor.value && Number(cursor.value.jobId) === jobId) {
          found.push(cursor.value);
        }
        cursor.continue();
      };
    })
  ));
}

async function readLocalUploadLogPageByStatusIndex(entries, jobId, rawStatuses, { page, pageSize }) {
  const safePageSize = Math.max(1, Number(pageSize || 25));
  const start = Math.max(0, Number(page || 0)) * safePageSize;
  const matched = [];
  const index = entries.index(ENTRY_JOB_STATUS_INDEX);
  await Promise.all(Array.from(rawStatuses).map((rawStatus) => (
    new Promise((resolve, reject) => {
      const request = index.openCursor(uploadJobStatusKeyRange(jobId, rawStatus));
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve();
          return;
        }
        matched.push(projectLocalLogEntry(cursor.value));
        cursor.continue();
      };
    })
  )));
  matched.sort(compareLocalLogs);
  return {
    allItems: matched.slice(start, start + safePageSize),
    countedTotal: matched.length,
    scannedAll: true,
  };
}

async function readLocalUploadLogPage(jobId, { page = 0, pageSize = 25, status = "", totalCountKnown = false } = {}) {
  const safePageSize = Math.max(1, Number(pageSize || 25));
  const start = Math.max(0, Number(page || 0)) * safePageSize;
  const rawStatuses = rawStatusesForLogFilter(status);
  return await withStore(ENTRY_STORE, "readonly", (entries) => (
    new Promise((resolve, reject) => {
      if (rawStatuses && entries.indexNames.contains(ENTRY_JOB_STATUS_INDEX)) {
        readLocalUploadLogPageByStatusIndex(entries, jobId, rawStatuses, { page, pageSize })
          .then(resolve)
          .catch(reject);
        return;
      }
      const pageItems = [];
      let matchedCount = 0;
      const request = entries.openCursor(uploadJobEntryKeyRange(jobId));
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve({
            allItems: pageItems,
            countedTotal: matchedCount,
            scannedAll: true,
          });
          return;
        }
        const entry = cursor.value;
        if (entry && Number(entry.jobId) === jobId) {
          if (!rawStatuses || rawStatuses.has(entry.clientStatus)) {
            if (matchedCount >= start && pageItems.length < safePageSize) {
              pageItems.push(projectLocalLogEntry(entry));
            }
            matchedCount += 1;
          }
        }
        if (pageItems.length >= safePageSize && (!status || totalCountKnown)) {
          resolve({
            allItems: pageItems,
            countedTotal: null,
            scannedAll: false,
          });
          return;
        }
        cursor.continue();
      };
    })
  ));
}

function localStatusCountsFromSummary(summary) {
  if (!summary) {
    return null;
  }
  const statusCounts = { PENDING: 0, IN_PROGRESS: 0, COMPLETE: 0, ERROR: 0, CANCELLED: 0, TIMED_OUT: 0, ABANDONED: 0 };
  statusCounts.COMPLETE = Number(summary.completedEntryCount || 0);
  const failed = Number(summary.failedEntryCount || 0);
  if (summary.clientJobStatus === CANCELLED_STATUS) {
    statusCounts.CANCELLED = failed;
  } else if (summary.clientJobStatus === TIMED_OUT_STATUS) {
    statusCounts.TIMED_OUT = failed;
  } else if (summary.clientJobStatus === ABANDONED_STATUS) {
    statusCounts.ABANDONED = failed;
  } else {
    statusCounts.ERROR = failed;
  }
  const accounted = statusCounts.COMPLETE + statusCounts.ERROR + statusCounts.CANCELLED + statusCounts.TIMED_OUT + statusCounts.ABANDONED;
  statusCounts.IN_PROGRESS = Math.max(0, Number(summary.totalEntryCount || 0) - accounted);
  return statusCounts;
}

function shouldRewriteLocalEntries(job) {
  return Number(job?.totalEntryCount || 0) <= LOCAL_ENTRY_REWRITE_LIMIT;
}

export async function persistUploadJob({ jobId, manifest, entries }) {
  return await observedIndexedDbOperation("indexeddb-persist-upload-job", {
    uploadJobId: jobId,
    targetFolderId: manifest?.folderId,
    schemaVersion: manifest?.schemaVersion,
    entryCount: entries?.length || 0,
  }, async () => {
    const now = new Date().toISOString();
    const firstFileEntry = entries?.find((entry) => entry.entryType === "FILE") || null;
    await withStore([JOB_STORE, ENTRY_STORE], "readwrite", ([jobs, uploadEntries]) => {
    jobs.put({
      jobId,
      folderId: manifest.folderId,
      schemaVersion: manifest.schemaVersion,
      createdAt: manifest.createdAt,
      lastUpdatedAt: now,
      clientJobStatus: "PENDING",
      keepAliveLastSentAt: null,
      totalEntryCount: entries?.length || 0,
      completedEntryCount: 0,
      failedEntryCount: 0,
      totalFileCount: entries?.filter((entry) => entry.entryType === "FILE").length || 0,
      completedFileCount: 0,
      failedFileCount: 0,
      totalFolderCount: entries?.filter((entry) => entry.entryType === "FOLDER").length || 0,
      completedFolderCount: 0,
      failedFolderCount: 0,
      totalBytes: entries?.reduce((total, entry) => total + (entry.entryType === "FILE" ? Number(entry.size || 0) : 0), 0) || 0,
      uploadedBytes: 0,
      progressPercent: null,
      progressUpdatedAt: null,
      retryOfJobId: manifest.retryOfJobId || null,
      retryAttempt: manifest.retryAttempt || 0,
      retentionSchedule: manifest?.retentionSchedule || firstFileEntry?.retentionSchedule || null,
      isSecret: manifest?.isSecret ?? firstFileEntry?.isSecret ?? null,
      keywords: manifest?.tags || firstFileEntry?.tags || [],
      metadata: manifest?.metadata || firstFileEntry?.metadata || [],
    });
    entries.forEach((entry) => {
      uploadEntries.put({
        jobId,
        clientEntryId: entry.clientEntryId,
        uploadJobLogId: entry.serverLogId,
        entryType: entry.entryType,
        relativePath: entry.relativePath,
        name: entry.name,
        size: entry.size,
        mimeType: entry.mimeType,
        clientStatus: entry.clientStatus,
        itemId: entry.itemId,
        uploadId: entry.uploadId,
        isSecret: entry.isSecret,
        retentionSchedule: entry.retentionSchedule,
        tags: entry.tags || [],
        metadata: entry.metadata || [],
        zeroByte: entry.zeroByte,
        originalClientEntryId: entry.originalClientEntryId || null,
        originalUploadJobLogId: entry.originalUploadJobLogId || null,
        errorCode: entry.errorCode,
        errorMessage: entry.errorMessage,
        sourceHandleId: entry.sourceHandleId || null,
        sourceHandleKind: entry.sourceHandleKind || (entry.entryType === "FILE" ? "FILE" : null),
        sourceHandlePermissionState: sourceHandlePermissionState(entry),
        sourceHandle: entry.sourceHandle || entry.sourceItem?.sourceHandle || null,
        sourceRootHandleId: entry.sourceRootHandleId || entry.sourceItem?.sourceRootHandleId || null,
        sourceRootHandleKind: entry.sourceRootHandleKind || entry.sourceItem?.sourceRootHandleKind || null,
        sourceRootHandle: entry.sourceRootHandle || entry.sourceItem?.sourceRootHandle || null,
        retryOfJobId: entry.retryOfJobId || null,
        retryAttempt: entry.retryAttempt || 0,
        uploadedBytes: 0,
        progressPercent: entry.entryType === "FILE" && entry.clientStatus === SUCCESS_STATUS ? 100 : null,
        progressUpdatedAt: null,
        attemptCount: 0,
        lastUpdatedAt: now,
      });
    });
  });
  });
}

export async function prepareLocalUploadJobRetry(jobId, manifest, entries) {
  const numericJobId = Number(jobId);
  if (!Number.isFinite(numericJobId)) {
    return;
  }
  const retryEntries = entries || [];
  const now = new Date().toISOString();
  return await observedIndexedDbOperation("indexeddb-prepare-upload-job-retry", {
    uploadJobId: numericJobId,
    entryCount: retryEntries.length,
  }, async () => await withStore([JOB_STORE, ENTRY_STORE], "readwrite", ([jobs, uploadEntries]) => {
    const jobRequest = jobs.get(numericJobId);
    jobRequest.onsuccess = () => {
      const currentJob = jobRequest.result || { jobId: numericJobId };
      jobs.put({
        ...currentJob,
        clientJobStatus: "PENDING",
        retryAttempt: manifest?.retryAttempt || (Number(currentJob.retryAttempt || 0) + 1),
        leaseStatus: null,
        leaseOwnerId: null,
        leaseLastSeenAt: null,
        lastUpdatedAt: now,
      });
    };
    retryEntries.forEach((retryEntry) => {
      if (!retryEntry?.clientEntryId) {
        return;
      }
      const entryRequest = uploadEntries.get([numericJobId, retryEntry.clientEntryId]);
      entryRequest.onsuccess = () => {
        const current = entryRequest.result || { jobId: numericJobId, clientEntryId: retryEntry.clientEntryId, attemptCount: 0 };
        if (current.clientStatus !== SUCCESS_STATUS) {
          const currentUploadedBytes = Number(current.uploadedBytes || 0);
          const shouldPreserveProgress = ["CANCELLED", "TIMED_OUT", "ABANDONED"].includes(current.clientStatus) && currentUploadedBytes > 0;
          uploadEntries.put({
            ...current,
            ...retryEntry,
            jobId: numericJobId,
            uploadJobLogId: retryEntry.serverLogId || retryEntry.uploadJobLogId || current.uploadJobLogId,
            clientStatus: "PENDING",
            errorCode: null,
            errorMessage: null,
            technicalMessage: null,
            uploadedBytes: shouldPreserveProgress ? currentUploadedBytes : 0,
            progressPercent: shouldPreserveProgress ? current.progressPercent : null,
            progressUpdatedAt: shouldPreserveProgress ? current.progressUpdatedAt : null,
            lastUpdatedAt: now,
          });
        }
      };
    });
  }));
}

export async function updateUploadJob(jobId, updates) {
  return await observedIndexedDbOperation("indexeddb-update-upload-job", {
    uploadJobId: jobId,
    clientJobStatus: updates?.clientJobStatus,
  }, async () => await withStore(JOB_STORE, "readwrite", (jobs) => {
    const request = jobs.get(jobId);
    request.onsuccess = () => {
      jobs.put({
        ...(request.result || { jobId }),
        ...updates,
        lastUpdatedAt: new Date().toISOString(),
      });
    };
  }));
}

export async function getUploadJobLocalSummary(jobId) {
  const numericJobId = Number(jobId);
  if (!Number.isFinite(numericJobId)) {
    return null;
  }
  return await withStore(JOB_STORE, "readonly", (jobs) => (
    requestToPromise(jobs.get(numericJobId)).then((job) => {
      if (!job || job.totalEntryCount === undefined) {
        return null;
      }
      return {
        clientJobStatus: visibleLocalJobStatus(job.clientJobStatus),
        totalEntryCount: job.totalEntryCount,
        completedEntryCount: job.completedEntryCount || 0,
        failedEntryCount: job.failedEntryCount || 0,
        totalFileCount: job.totalFileCount || 0,
        completedFileCount: job.completedFileCount || 0,
        failedFileCount: job.failedFileCount || 0,
        totalFolderCount: job.totalFolderCount || 0,
        completedFolderCount: job.completedFolderCount || 0,
        failedFolderCount: job.failedFolderCount || 0,
        totalBytes: job.totalBytes || 0,
        uploadedBytes: job.uploadedBytes || 0,
        progressPercent: job.progressPercent,
        progressUpdatedAt: job.progressUpdatedAt,
        retentionSchedule: job.retentionSchedule || null,
        isSecret: job.isSecret,
        keywords: job.keywords || [],
        metadata: job.metadata || [],
      };
    })
  ));
}

export async function getUploadJobLocalEntrySummary(jobId) {
  const numericJobId = Number(jobId);
  if (!Number.isFinite(numericJobId)) {
    return null;
  }
  const job = await withStore(JOB_STORE, "readonly", (jobs) => requestToPromise(jobs.get(numericJobId)));
  if (!job) {
    return null;
  }
  const uploadEntries = await readLocalUploadEntries(numericJobId);
  if (!uploadEntries.length) {
    return null;
  }
  return summarizeLocalEntries(uploadEntries, job);
}

export async function getUploadJobLocalLogs(jobId, { page = 0, pageSize = 25, status = "", summary = null } = {}) {
  const numericJobId = Number(jobId);
  if (!Number.isFinite(numericJobId)) {
    return null;
  }
  const job = await withStore(JOB_STORE, "readonly", (jobs) => requestToPromise(jobs.get(numericJobId)));
  if (!job) {
    return null;
  }
  const statusCounts = localStatusCountsFromSummary(summary) || localStatusCountsFromSummary({
    ...job,
    clientJobStatus: job.clientJobStatus,
  });
  const totalCount = Number(summary?.totalEntryCount ?? job.totalEntryCount ?? 0);
  const pageResult = await readLocalUploadLogPage(numericJobId, {
    page,
    pageSize,
    status,
    totalCountKnown: !!statusCounts && !status,
  });
  if (!pageResult.allItems.length && totalCount === 0 && !pageResult.scannedAll) {
    return null;
  }
  if (!pageResult.allItems.length && pageResult.scannedAll && pageResult.countedTotal === 0 && totalCount === 0) {
    return null;
  }
  const filteredCount = status
    ? Number(statusCounts?.[status] ?? pageResult.countedTotal ?? 0)
    : totalCount || pageResult.countedTotal || pageResult.allItems.length;
  if (page === 0 && !pageResult.allItems.length && pageResult.scannedAll && filteredCount > 0) {
    uploadDiagnosticLog("info", "my-uploads-local-log-backend-fallback", {
      uploadJobId: numericJobId,
      reason: "summary-without-local-entries",
      status,
      filteredCount,
    });
    return null;
  }
  return {
    totalItemCount: filteredCount,
    allItems: pageResult.allItems,
    statusCounts: statusCounts || { PENDING: 0, IN_PROGRESS: 0, COMPLETE: 0, ERROR: 0, CANCELLED: 0, TIMED_OUT: 0, ABANDONED: 0 },
  };
}

export async function getUploadJobLocalLogEntries(jobId, clientEntryIds = []) {
  const numericJobId = Number(jobId);
  const ids = Array.from(new Set(clientEntryIds.filter(Boolean)));
  if (!Number.isFinite(numericJobId) || !ids.length) {
    return [];
  }
  return await withStore(ENTRY_STORE, "readonly", async (entries) => {
    const localEntries = await Promise.all(ids.map((clientEntryId) =>
      requestToPromise(entries.get([numericJobId, clientEntryId]))
    ));
    return localEntries
      .filter(Boolean)
      .map(projectLocalLogEntry);
  });
}

export async function getRetryableLocalUploadEntries(jobId) {
  const numericJobId = Number(jobId);
  if (!Number.isFinite(numericJobId)) {
    return [];
  }
  const entries = await readLocalUploadEntries(numericJobId);
  return entries
    .filter((entry) => entry.clientStatus !== SUCCESS_STATUS)
    .sort((left, right) => {
      if (left.entryType !== right.entryType) {
        return left.entryType === "FOLDER" ? -1 : 1;
      }
      return String(left.relativePath || "").localeCompare(String(right.relativePath || ""));
    });
}

export async function startLocalUploadLease(jobId) {
  const ownerId = newLeaseOwnerId();
  uploadDiagnosticLog("info", "local-lease-start", { uploadJobId: jobId, leaseOwnerId: ownerId });
  await touchLocalUploadLease(jobId, ownerId);
  return ownerId;
}

export async function touchLocalUploadLease(jobId, ownerId) {
  const now = new Date().toISOString();
  uploadDiagnosticLogThrottled("debug", "local-lease-touch", jobId, { uploadJobId: jobId, leaseOwnerId: ownerId });
  await updateUploadJob(jobId, {
    leaseOwnerId: ownerId,
    leaseLastSeenAt: now,
    leaseStatus: "ACTIVE",
  });
}

export async function clearLocalUploadLease(jobId, ownerId) {
  return await observedIndexedDbOperation("local-lease-clear", {
    uploadJobId: jobId,
    leaseOwnerId: ownerId,
  }, async () => {
    const now = new Date().toISOString();
    await withStore(JOB_STORE, "readwrite", (jobs) => {
    const request = jobs.get(jobId);
    request.onsuccess = () => {
      const current = request.result || { jobId };
      if (ownerId && current.leaseOwnerId && current.leaseOwnerId !== ownerId) {
        return;
      }
      jobs.put({
        ...current,
        leaseOwnerId: null,
        leaseStatus: null,
        leaseLastSeenAt: now,
        lastUpdatedAt: now,
      });
    };
    });
  });
}

export async function getAbandonedLocalUploadJobs(timeoutMs = DEFAULT_LOCAL_LEASE_TIMEOUT_MS) {
  const cutoff = Date.now() - timeoutMs;
  const abandonedJobs = await withStore(JOB_STORE, "readonly", (jobs) => (
    new Promise((resolve, reject) => {
      const abandoned = [];
      const request = jobs.openCursor();
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve(abandoned);
          return;
        }
        const job = cursor.value;
        const lastSeen = Date.parse(job.leaseLastSeenAt || 0);
        const lastUpdated = Date.parse(job.lastUpdatedAt || 0);
        if (
          job?.leaseStatus === "ACTIVE" &&
          !TERMINAL_JOB_STATUSES.has(job?.clientJobStatus) &&
          (!lastSeen || lastSeen < cutoff) &&
          (!lastUpdated || lastUpdated < cutoff)
        ) {
          abandoned.push(job);
        }
        cursor.continue();
      };
    })
  ));
  if (abandonedJobs.length) {
    uploadDiagnosticLog("warn", "local-lease-stale-detected", {
      staleLeaseCount: abandonedJobs.length,
      timeoutMs,
      uploadJobIds: abandonedJobs.map((job) => job.jobId),
    });
  }
  return abandonedJobs;
}

export async function updateUploadEntry(jobId, clientEntryId, updates) {
  return await observedIndexedDbOperation("indexeddb-update-upload-entry", {
    uploadJobId: jobId,
    clientEntryId,
    clientStatus: updates?.clientStatus,
    itemId: updates?.itemId,
  }, async () => await withStore(ENTRY_STORE, "readwrite", (entries) => {
    const request = entries.get([jobId, clientEntryId]);
    request.onsuccess = () => {
      const current = request.result || { jobId, clientEntryId, attemptCount: 0 };
      entries.put(mergeEntryUpdate(current, updates));
    };
  }));
}

export async function updateUploadEntryProgress(jobId, clientEntryId, uploadedBytes, totalBytes) {
  const progress = clampByteProgress(uploadedBytes, totalBytes);
  return await observedIndexedDbOperation("indexeddb-update-upload-entry-progress", {
    uploadJobId: jobId,
    clientEntryId,
    uploadedBytes: progress.uploadedBytes,
    progressPercent: progress.progressPercent,
  }, async () => await withStore(ENTRY_STORE, "readwrite", (entries) => {
    const request = entries.get([jobId, clientEntryId]);
    request.onsuccess = () => {
      const current = request.result || { jobId, clientEntryId, attemptCount: 0 };
      const size = Number(current.size || totalBytes || 0);
      const clamped = clampByteProgress(progress.uploadedBytes, size);
      entries.put({
        ...current,
        uploadedBytes: clamped.uploadedBytes,
        progressPercent: clamped.progressPercent,
        progressUpdatedAt: new Date().toISOString(),
        lastUpdatedAt: new Date().toISOString(),
      });
    };
  }));
}

export async function updateUploadEntries(jobId, entryUpdates) {
  if (!entryUpdates?.length) {
    return;
  }
  return await observedIndexedDbOperation("indexeddb-update-upload-entries", {
    uploadJobId: jobId,
    entryCount: entryUpdates.length,
  }, async () => await withStore(ENTRY_STORE, "readwrite", async (entries) => {
    await Promise.all(entryUpdates.map(async ({ clientEntryId, updates }) => {
      const current = await requestToPromise(entries.get([jobId, clientEntryId]));
      entries.put(mergeEntryUpdate(
        current || { jobId, clientEntryId, attemptCount: 0 },
        updates
      ));
    }));
  }));
}

export async function markLocalUploadEntriesRetryPending(jobId, clientEntryIds) {
  const numericJobId = Number(jobId);
  const ids = [...new Set((clientEntryIds || []).filter(Boolean))];
  if (!Number.isFinite(numericJobId) || !ids.length) {
    return;
  }
  const now = new Date().toISOString();
  return await observedIndexedDbOperation("indexeddb-mark-retry-entries-pending", {
    uploadJobId: numericJobId,
    entryCount: ids.length,
  }, async () => await withStore(ENTRY_STORE, "readwrite", (entries) => {
    ids.forEach((clientEntryId) => {
      const request = entries.get([numericJobId, clientEntryId]);
      request.onsuccess = () => {
        const current = request.result;
        if (!current || current.clientStatus === SUCCESS_STATUS) {
          return;
        }
        entries.put({
          ...current,
          clientStatus: "PENDING",
          uploadedBytes: 0,
          progressPercent: null,
          progressUpdatedAt: null,
          errorCode: null,
          errorMessage: null,
          technicalMessage: null,
          attemptCount: (current.attemptCount || 0) + 1,
          lastUpdatedAt: now,
        });
      };
    });
  }));
}

export async function cancelLocalUploadJob(jobId) {
  const numericJobId = Number(jobId);
  if (!Number.isFinite(numericJobId)) {
    return;
  }
  const now = new Date().toISOString();
  uploadDiagnosticLog("info", "local-upload-cancel-requested", { uploadJobId: numericJobId });
  await withStore([JOB_STORE, ENTRY_STORE], "readwrite", ([jobs, entries]) => {
    const jobRequest = jobs.get(numericJobId);
    jobRequest.onsuccess = () => {
      const currentJob = jobRequest.result || { jobId: numericJobId };
      jobs.put({
        ...currentJob,
        clientJobStatus: CANCELLED_STATUS,
        lastUpdatedAt: now,
      });
      if (!shouldRewriteLocalEntries(currentJob)) {
        uploadDiagnosticLog("info", "local-upload-cancel-entry-rewrite-skipped", {
          uploadJobId: numericJobId,
          totalEntryCount: currentJob.totalEntryCount,
        });
        return;
      }
      const entryCursorRequest = entries.openCursor(uploadJobEntryKeyRange(numericJobId));
      entryCursorRequest.onsuccess = (event) => {
        const cursor = event.target.result;
        if (!cursor) {
          return;
        }
        if (Number(cursor.value?.jobId) === numericJobId && !TERMINAL_ENTRY_STATUSES.has(cursor.value?.clientStatus)) {
          entries.put({
            ...cursor.value,
            clientStatus: CANCELLED_STATUS,
            lastUpdatedAt: now,
          });
        }
        cursor.continue();
      };
    };
  });
}

export async function markLocalUploadJobCancelling(jobId) {
  const numericJobId = Number(jobId);
  if (!Number.isFinite(numericJobId)) {
    return;
  }
  const now = new Date().toISOString();
  uploadDiagnosticLog("info", "local-upload-cancelling", { uploadJobId: numericJobId });
  await withStore([JOB_STORE, ENTRY_STORE], "readwrite", ([jobs, entries]) => {
    const jobRequest = jobs.get(numericJobId);
    jobRequest.onsuccess = () => {
      const currentJob = jobRequest.result || { jobId: numericJobId };
      jobs.put({
        ...currentJob,
        clientJobStatus: CANCELLING_STATUS,
        lastUpdatedAt: now,
      });
      if (!shouldRewriteLocalEntries(currentJob)) {
        uploadDiagnosticLog("info", "local-upload-cancelling-entry-rewrite-skipped", {
          uploadJobId: numericJobId,
          totalEntryCount: currentJob.totalEntryCount,
        });
        return;
      }
      const entryCursorRequest = entries.openCursor(uploadJobEntryKeyRange(numericJobId));
      entryCursorRequest.onsuccess = (event) => {
        const cursor = event.target.result;
        if (!cursor) {
          return;
        }
        if (Number(cursor.value?.jobId) === numericJobId && !TERMINAL_ENTRY_STATUSES.has(cursor.value?.clientStatus)) {
          entries.put({
            ...cursor.value,
            clientStatus: CANCELLING_STATUS,
            lastUpdatedAt: now,
          });
        }
        cursor.continue();
      };
    };
  });
}

export async function timeOutLocalUploadJob(jobId) {
  return markLocalUploadJobTerminal(jobId, TIMED_OUT_STATUS, "local-upload-timed-out", "local-upload-timeout-entry-rewrite-skipped");
}

export async function abandonLocalUploadJob(jobId) {
  return markLocalUploadJobTerminal(jobId, ABANDONED_STATUS, "local-upload-abandoned", "local-upload-abandon-entry-rewrite-skipped");
}

async function markLocalUploadJobTerminal(jobId, terminalStatus, eventName, skippedEventName) {
  const numericJobId = Number(jobId);
  if (!Number.isFinite(numericJobId)) {
    return;
  }
  const now = new Date().toISOString();
  uploadDiagnosticLog("warn", eventName, { uploadJobId: numericJobId });
  await withStore([JOB_STORE, ENTRY_STORE], "readwrite", ([jobs, entries]) => {
    const jobRequest = jobs.get(numericJobId);
    jobRequest.onsuccess = () => {
      const currentJob = jobRequest.result || { jobId: numericJobId };
      jobs.put({
        ...currentJob,
        clientJobStatus: terminalStatus,
        leaseOwnerId: null,
        leaseStatus: null,
        leaseLastSeenAt: now,
        lastUpdatedAt: now,
      });
      if (!shouldRewriteLocalEntries(currentJob)) {
        uploadDiagnosticLog("info", skippedEventName, {
          uploadJobId: numericJobId,
          totalEntryCount: currentJob.totalEntryCount,
        });
        return;
      }
      const entryCursorRequest = entries.openCursor(uploadJobEntryKeyRange(numericJobId));
      entryCursorRequest.onsuccess = (event) => {
        const cursor = event.target.result;
        if (!cursor) {
          return;
        }
        if (Number(cursor.value?.jobId) === numericJobId && !TERMINAL_ENTRY_STATUSES.has(cursor.value?.clientStatus)) {
          entries.put({
            ...cursor.value,
            clientStatus: terminalStatus,
            lastUpdatedAt: now,
          });
        }
        cursor.continue();
      };
    };
  });
}

export async function isUploadJobCancellationRequested(jobId) {
  const numericJobId = Number(jobId);
  if (!Number.isFinite(numericJobId)) {
    return false;
  }
  return await withStore(JOB_STORE, "readonly", (jobs) => (
    requestToPromise(jobs.get(numericJobId)).then((job) => (
      job?.clientJobStatus === CANCELLING_STATUS ||
      job?.clientJobStatus === CANCELLED_STATUS ||
      job?.clientJobStatus === TIMED_OUT_STATUS ||
      job?.clientJobStatus === ABANDONED_STATUS
    ))
  ));
}

export async function reconcileLocalUploadJobWithServer(job) {
  const numericJobId = Number(job?.id || job?.jobId);
  const serverStatus = job?.status;
  if (!Number.isFinite(numericJobId) || !TERMINAL_JOB_STATUSES.has(serverStatus)) {
    return;
  }
  const entryStatus = entryStatusForTerminalJob(serverStatus);
  const now = new Date().toISOString();
  await withStore([JOB_STORE, ENTRY_STORE], "readwrite", ([jobs, entries]) => {
    const jobRequest = jobs.get(numericJobId);
    jobRequest.onsuccess = () => {
      const currentJob = jobRequest.result || { jobId: numericJobId };
      jobs.put({
        ...currentJob,
        clientJobStatus: serverStatus,
        lastUpdatedAt: now,
      });
      if (!shouldRewriteLocalEntries(currentJob)) {
        uploadDiagnosticLog("info", "local-upload-reconcile-entry-rewrite-skipped", {
          uploadJobId: numericJobId,
          serverStatus,
          totalEntryCount: currentJob.totalEntryCount,
        });
        return;
      }
      const cursorRequest = entries.openCursor(uploadJobEntryKeyRange(numericJobId));
      cursorRequest.onsuccess = (event) => {
        const cursor = event.target.result;
        if (!cursor) {
          return;
        }
        if (Number(cursor.value?.jobId) === numericJobId && !TERMINAL_ENTRY_STATUSES.has(cursor.value?.clientStatus)) {
          entries.put({
            ...cursor.value,
            clientStatus: entryStatus,
            lastUpdatedAt: now,
          });
        }
        cursor.continue();
      };
    };
  });
}

export async function deleteUploadJob(jobId) {
  const numericJobId = Number(jobId);
  if (!Number.isFinite(numericJobId)) {
    return;
  }
  await withStore([JOB_STORE, ENTRY_STORE], "readwrite", ([jobs, entries]) => {
    const entryCursorRequest = entries.openCursor(uploadJobEntryKeyRange(numericJobId));
    entryCursorRequest.onsuccess = (event) => {
      const cursor = event.target.result;
      if (!cursor) {
        return;
      }
      if (Number(cursor.value?.jobId) === numericJobId) {
        cursor.delete();
      }
      cursor.continue();
    };
    const jobCursorRequest = jobs.openCursor();
    jobCursorRequest.onsuccess = (event) => {
      const cursor = event.target.result;
      if (!cursor) {
        return;
      }
      if (Number(cursor.value?.jobId) === numericJobId) {
        cursor.delete();
        return;
      }
      cursor.continue();
    };
  });
}

export async function deleteSuccessfulUploadEntries(jobId) {
  const numericJobId = Number(jobId);
  if (!Number.isFinite(numericJobId)) {
    return;
  }
  await withStore(ENTRY_STORE, "readwrite", (entries) => {
    const cursorSource = entries.indexNames.contains(ENTRY_JOB_STATUS_INDEX)
      ? entries.index(ENTRY_JOB_STATUS_INDEX)
      : entries;
    const entryCursorRequest = entries.indexNames.contains(ENTRY_JOB_STATUS_INDEX)
      ? cursorSource.openCursor(uploadJobStatusKeyRange(numericJobId, SUCCESS_STATUS))
      : cursorSource.openCursor(uploadJobEntryKeyRange(numericJobId));
    entryCursorRequest.onsuccess = (event) => {
      const cursor = event.target.result;
      if (!cursor) {
        return;
      }
      if (Number(cursor.value?.jobId) === numericJobId && cursor.value?.clientStatus === SUCCESS_STATUS) {
        cursor.delete();
      }
      cursor.continue();
    };
  });
}

export async function getUploadJobSnapshot(jobId) {
  const numericJobId = Number(jobId);
  if (!Number.isFinite(numericJobId)) {
    return null;
  }
  const startedAt = uploadDiagnosticStart();
  const job = await withStore(JOB_STORE, "readonly", (jobs) => requestToPromise(jobs.get(numericJobId)));
  if (!job) {
    return null;
  }
  const uploadEntries = await readLocalUploadEntries(numericJobId);
  const durationMs = uploadDiagnosticDurationMs(startedAt);
  if (durationMs > SLOW_LOCAL_SNAPSHOT_READ_MS && uploadEntries.length > 0) {
    uploadDiagnosticLogThrottled("warn", "indexeddb-local-snapshot-slow", "global", {
      uploadJobId: numericJobId,
      entryCount: uploadEntries.length,
      durationMs,
    }, SLOW_LOCAL_SNAPSHOT_LOG_THROTTLE_MS);
  }
  if (!uploadEntries.length) {
    return null;
  }
  return { job, entries: uploadEntries };
}

export function getUploadRetryPlan(snapshot) {
  const jobStatus = snapshot?.job?.clientJobStatus;
  const entries = snapshot?.entries || [];
  const retryableEntries = entries.filter((entry) => entry.clientStatus !== SUCCESS_STATUS);
  const retryableFiles = retryableEntries.filter((entry) => entry.entryType === "FILE");
  const missingFileAccess = retryableFiles.filter((entry) => (
    entry.sourceHandlePermissionState !== SOURCE_HANDLE_PERMISSION.AVAILABLE
  ));
  const unsupportedFileAccess = missingFileAccess.filter((entry) => (
    entry.sourceHandlePermissionState === SOURCE_HANDLE_PERMISSION.UNSUPPORTED
  ));

  if (!RETRYABLE_JOB_STATUSES.has(jobStatus)) {
    return {
      eligible: false,
      canStartFromDurableHandles: false,
      reason: TERMINAL_JOB_STATUSES.has(jobStatus) ? "JOB_NOT_RETRYABLE" : "JOB_NOT_TERMINAL",
      retryableEntries,
      missingFileAccess,
    };
  }
  if (!retryableEntries.length) {
    return {
      eligible: false,
      canStartFromDurableHandles: false,
      reason: "NO_INCOMPLETE_ENTRIES",
      retryableEntries,
      missingFileAccess,
    };
  }
  return {
    eligible: true,
    canStartFromDurableHandles: missingFileAccess.length === 0,
    reason: missingFileAccess.length ? "FILE_ACCESS_REQUIRED" : "READY",
    retryableEntries,
    completedEntries: entries.filter((entry) => entry.clientStatus === SUCCESS_STATUS),
    missingFileAccess,
    unsupportedFileAccess,
  };
}

export async function cleanupTerminalUploadJobs(retentionDays = 14) {
  const cutoff = Date.now() - retentionDays * 24 * 60 * 60 * 1000;
  await withStore([JOB_STORE, ENTRY_STORE], "readwrite", ([jobs, entries]) => {
    const cursorRequest = jobs.openCursor();
    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result;
      if (!cursor) {
        return;
      }
      const job = cursor.value;
      const updatedAt = Date.parse(job.lastUpdatedAt || job.createdAt || 0);
      if (job.clientJobStatus === SUCCESS_STATUS && updatedAt < cutoff) {
        entries.openCursor(uploadJobEntryKeyRange(job.jobId)).onsuccess = (event) => {
          const entryCursor = event.target.result;
          if (!entryCursor) {
            return;
          }
          if (entryCursor.value.jobId === job.jobId) {
            entryCursor.delete();
          }
          entryCursor.continue();
        };
        cursor.delete();
      }
      cursor.continue();
    };
  });
}
